import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { parseRoute, routeHash, type AppRoute, type NavigationEntry } from './routes';
export type { AppRoute, NavigationEntry } from './routes';
type BackHandler = () => boolean;

interface NavigationContextValue {
  current: NavigationEntry;
  canGoBack: boolean;
  navigate: (route: AppRoute, params?: Record<string, unknown>) => void;
  replace: (route: AppRoute, params?: Record<string, unknown>) => void;
  switchTab: (route: AppRoute) => void;
  back: () => boolean;
  reset: (route?: AppRoute) => void;
  registerBackHandler: (handler: BackHandler) => () => void;
}

const AppNavigationContext = createContext<NavigationContextValue | null>(null);
const HISTORY_KEY = 'xbbWebNavigation';
interface HistoryPosition {
  index: number;
}
const readPosition = (): HistoryPosition | undefined => {
  const value = window.history.state?.[HISTORY_KEY];
  return value && Number.isSafeInteger(value.index) && value.index >= 0 ? value : undefined;
};
export const AppNavigationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [current, setCurrent] = useState<NavigationEntry>(() => parseRoute(window.location.hash));
  const currentRef = useRef(current);
  const position = useRef(readPosition()?.index ?? 0);
  const [canGoBack, setCanGoBack] = useState(position.current > 0);
  const restoring = useRef(false);
  const handlersRef = useRef<BackHandler[]>([]);
  const consumeBack = useCallback(() => {
    for (let i = handlersRef.current.length - 1; i >= 0; i--)
      if (handlersRef.current[i]()) return true;
    return false;
  }, []);
  const registerBackHandler = useCallback((handler: BackHandler) => {
    handlersRef.current.push(handler);
    return () => {
      handlersRef.current = handlersRef.current.filter((item) => item !== handler);
    };
  }, []);
  const commit = useCallback((entry: NavigationEntry, index: number, push: boolean) => {
    const state = { ...window.history.state, [HISTORY_KEY]: { index } };
    window.history[push ? 'pushState' : 'replaceState'](state, '', routeHash(entry));
    position.current = index;
    currentRef.current = entry;
    setCurrent(entry);
    setCanGoBack(index > 0);
  }, []);
  useEffect(() => {
    commit(currentRef.current, position.current, false);
    const onPop = () => {
      const next = readPosition();
      if (restoring.current) {
        restoring.current = false;
        return;
      }
      if (next && consumeBack()) {
        const delta = position.current - next.index;
        if (delta) {
          restoring.current = true;
          window.history.go(delta);
        } else commit(currentRef.current, position.current, false);
        return;
      }
      commit(parseRoute(window.location.hash), next?.index ?? 0, false);
    };
    const onHash = () => {
      if (restoring.current) return;
      const entry = parseRoute(window.location.hash);
      if (routeHash(entry) === routeHash(currentRef.current)) return;
      if (consumeBack()) {
        commit(currentRef.current, position.current, false);
        return;
      }
      commit(entry, position.current + 1, false);
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
    };
  }, [commit, consumeBack]);
  const navigate = useCallback(
    (route: AppRoute, params?: Record<string, unknown>) => {
      const entry = { route, params };
      if (routeHash(entry) !== routeHash(currentRef.current))
        commit(entry, position.current + 1, true);
    },
    [commit],
  );
  const replace = useCallback(
    (route: AppRoute, params?: Record<string, unknown>) =>
      commit({ route, params }, position.current, false),
    [commit],
  );
  const reset = useCallback((route: AppRoute = 'diary') => commit({ route }, 0, false), [commit]);
  const switchTab = navigate;
  const back = useCallback(() => {
    if (consumeBack()) return true;
    if (position.current > 0) {
      window.history.back();
      return true;
    }
    if (currentRef.current.route !== 'diary') {
      reset();
      return true;
    }
    return false;
  }, [consumeBack, reset]);
  useEffect(() => {
    const onExternalRoute = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      if (typeof detail?.route !== 'string') return;
      const entry = parseRoute(detail.route);
      navigate(entry.route, entry.params);
    };
    window.addEventListener('xbb:navigate', onExternalRoute);
    return () => window.removeEventListener('xbb:navigate', onExternalRoute);
  }, [navigate]);
  const value: NavigationContextValue = {
    current,
    canGoBack,
    navigate,
    replace,
    switchTab,
    back,
    reset,
    registerBackHandler,
  };
  return <AppNavigationContext.Provider value={value}>{children}</AppNavigationContext.Provider>;
};

export const useAppNavigation = () => {
  const context = useContext(AppNavigationContext);
  if (!context) throw new Error('useAppNavigation must be used inside AppNavigationProvider');
  return context;
};

export const useAppBackHandler = (handler: BackHandler, enabled = true) => {
  const { registerBackHandler } = useAppNavigation();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return registerBackHandler(() => handlerRef.current());
  }, [enabled, registerBackHandler]);
};
